import { useState, useRef, useMemo } from 'react';
import { Avatar, AlertDialog, Button, Flex, Box } from '@radix-ui/themes';
import { Pencil1Icon } from '@radix-ui/react-icons';
import api from '../../api';
import { toast } from 'react-toastify';
import { updateUserDetails } from '../../redux/slices/userSlice';
import { useDispatch } from 'react-redux';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const ProfilePictureForm = ({ user, size = '4', radius = 'large' }) => {
    const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL;
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const imgRef = useRef(null); // Reference to the image element
    const [isConfirmProfileChange, setIsConfirmProfileChange] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [crop, setCrop] = useState({ unit: '%', width: 50, height: 50, x: 25, y: 25 });
    const [completedCrop, setCompletedCrop] = useState(null); // Store completed crop
    const [croppedImage, setCroppedImage] = useState(null);

    if (!user) return null;

    const handleUpload = async (file) => {
        if (!file) {
            toast.error("Please select an image first.", { position: "bottom-right" });
            return;
        }

        console.log('File to upload:', file);
        const formData = new FormData();
        formData.append("image", file, "cropped-image.jpg");

        try {
            const response = await api.post(`/user/profile-pic/${user.id}`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            dispatch(updateUserDetails(response.data.user));
            toast.success("Profile picture updated", { position: "bottom-right" });
        } catch (error) {
            console.error("Upload failed:", error.response?.data || error);
            toast.error("Failed to update profile picture", { position: "bottom-right" });
        } finally {
            resetFileInput();
        }
    };

    const handleEditProfilePicClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setIsConfirmProfileChange(true);
        }
    };

    const resetFileInput = () => {
        fileInputRef.current.value = null;
        setSelectedFile(null);
        setCroppedImage(null);
        setCompletedCrop(null);
    };

    const getCroppedImg = async (image, crop) => {
        if (!image || !crop) {
            throw new Error('Image or crop data missing');
        }

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        const offscreen = new OffscreenCanvas(
            crop.width * scaleX,
            crop.height * scaleY
        );
        const ctx = offscreen.getContext('2d');
        if (!ctx) {
            throw new Error('No 2d context');
        }

        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width * scaleX,
            crop.height * scaleY
        );

        const blob = await offscreen.convertToBlob({
            type: 'image/jpeg',
            quality: 0.95, // Adjust quality if needed
        });

        console.log('Cropped Blob:', blob);
        return blob;
    };

    const onCropComplete = async (crop, percentCrop) => {
        setCompletedCrop(crop); // Store the completed crop
    };

    const handleConfirm = async () => {
        if (imgRef.current && completedCrop) {
            const croppedBlob = await getCroppedImg(imgRef.current, completedCrop);
            setCroppedImage(croppedBlob);
            setIsConfirmProfileChange(false);
            await handleUpload(croppedBlob);
        } else {
            setIsConfirmProfileChange(false);
            await handleUpload(selectedFile); // Fallback to original if no crop
        }
    };

    // Set initial crop when image loads
    const handleImageLoad = () => {
        if (imgRef.current) {
            const { width, height } = imgRef.current;
            const cropSize = Math.min(width, height) * 0.5; // 50% of the smaller dimension
            console.log('setting crop')
            setCrop({
                unit: 'px',
                width: cropSize,
                height: cropSize,
                x: (width - cropSize) / 2,
                y: (height - cropSize) / 2,
            });
        }
    };

    const imageSrc = useMemo(() => {
        return selectedFile ? URL.createObjectURL(selectedFile) : null;
    }, [selectedFile]);

    return (
        <Flex direction="column" justify="center" gap="4" align="center" width="100%">
            <Flex direction="column" justify="center" gap="4">
                <Avatar
                    size={size}
                    src={`${PUBLIC_URL}/storage/${user.profile_picture}`}
                    fallback={user.firstname?.[0]}
                    radius={radius}
                />
                <Button
                    variant="ghost"
                    size="1"
                    onClick={handleEditProfilePicClick}
                >
                    <Pencil1Icon size={16} /> Change
                </Button>
            </Flex>
            <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
            />

            <AlertDialog.Root open={isConfirmProfileChange} onOpenChange={setIsConfirmProfileChange}>
                <AlertDialog.Content className="p-4 rounded shadow-lg w-1/3 mx-auto">
                    <AlertDialog.Title>Change Profile Picture</AlertDialog.Title>
                    <AlertDialog.Description>
                        Crop and confirm your new profile picture.
                    </AlertDialog.Description>

                    {selectedFile && (
                        <div className="relative w-full max-w-lg mx-auto my-4">
                            <ReactCrop
                                crop={crop}
                                onChange={(_, percentCrop) => setCrop(percentCrop)}
                                onComplete={onCropComplete}
                                aspect={1}
                            >
                                <img
                                    ref={imgRef}
                                    src={imageSrc}
                                    alt="Crop preview"
                                    className="w-full h-auto max-w-full object-contain"
                                    onLoad={handleImageLoad}
                                // onLoad={() => console.log('Image loaded:', imgRef.current)}
                                />
                            </ReactCrop>
                        </div>
                    )}

                    <div className="flex justify-end gap-4 mt-4">
                        <Button
                            variant="soft"
                            onClick={() => {
                                setIsConfirmProfileChange(false);
                                resetFileInput();
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            color="green"
                            onClick={handleConfirm}
                        >
                            Confirm
                        </Button>
                    </div>
                </AlertDialog.Content>
            </AlertDialog.Root>
        </Flex>
    );
};

export default ProfilePictureForm;